<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { DocCategory } from '@/types/docs'

const props = defineProps<{
  category: DocCategory
  articleCount: number
  base: string
}>()

const { t } = useI18n()

const categoryIcons: Record<DocCategory, string> = {
  [DocCategory.GettingStarted]: 'M13 10V3L4 14h7v7l9-11h-7z',
  [DocCategory.EventsEditions]: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  [DocCategory.RegistrationForms]: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  [DocCategory.PricingDiscounts]: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
  [DocCategory.ParticipantManagement]: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  [DocCategory.PaymentsFinance]: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
  [DocCategory.PublicEventWebsite]: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
  [DocCategory.OrganisationSettings]: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  [DocCategory.Newsletters]: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  [DocCategory.ApiIntegrations]: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
}

const iconPath = computed(() => categoryIcons[props.category])

const hasArticles = computed(() => props.articleCount > 0)

const i18nKey = computed(() => {
  const safe = props.category.replace(/-/g, '_')
  return safe
})

function getCategoryKey(suffix: string): string {
  return `docs.category.${props.category}.${suffix}`
}
</script>

<template>
  <component
    :is="hasArticles ? RouterLink : 'div'"
    :to="hasArticles ? `${base}/${category}` : undefined"
    class="group block rounded-xl bg-slate-800/50 border border-slate-700 p-6 transition-all"
    :class="hasArticles
      ? 'hover:bg-slate-800 hover:border-slate-600 cursor-pointer'
      : 'cursor-default opacity-60'"
  >
    <!-- Icon -->
    <div class="mb-4 w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
      <svg
        class="w-5 h-5 text-emerald-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          :d="iconPath"
        />
      </svg>
    </div>

    <!-- Title -->
    <h3 class="text-sm font-semibold text-white mb-1">
      {{ t(getCategoryKey('title')) }}
    </h3>

    <!-- Blurb -->
    <p class="text-xs text-slate-400 mb-3 leading-relaxed">
      {{ t(getCategoryKey('blurb')) }}
    </p>

    <!-- Article count / coming soon -->
    <span
      v-if="hasArticles"
      class="text-xs text-emerald-400 font-medium"
    >
      {{ t('docs.hub.article_count', { count: articleCount }, articleCount) }}
    </span>
    <span
      v-else
      class="text-xs text-slate-500 font-medium"
    >
      {{ t('docs.hub.coming_soon') }}
    </span>
  </component>
</template>
